import React, { useEffect, useMemo } from 'react'
import {
  GetUserNotifications$Request,
  NotificationId,
  NotificationType,
  UserNotificationDto,
} from '../../services/api.types'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import { Card } from '../../components/Card/Card'
import { t } from 'ttag'
import { isRtl } from '../../i18nInit'
import Loader from '../../components/Loader/Loader'
import UserNotificationWrapper from './components/UserNotification/UserNotificationWrapper'
import YumButton from '../../components/YumButton/YumButton'
import { isNative } from '../../common/helpers/isNative'
import YumEmptyState from '../../components/YumEmptyState/YumEmptyState'
import YumTabs from '../../components/YumTabs/YumTabs'
import YumTab from '../../components/YumTabs/YumTab/YumTab'

import { ReactComponent as EmptyStateIcon } from '../../assets/v3/icons/notifications/empty-state.svg'
import YumHeader from '../../components/YumHeader/YumHeader'
import Box from '@material-ui/core/Box'
import { ReactComponent as FilterIconNew } from '../../assets/v3/icons/ranking/filter.svg'
import { ROUTES } from '../../common/Routes/routePaths'
import { PluginListenerHandle } from '@capacitor/core'
import { App } from '@capacitor/app'

export interface IInboxProps {
  notificationId: NotificationId | null
  userNotifications: UserNotificationDto[]
  isLoading: boolean
  isPush: boolean
  feedFilter: NotificationType
}

export interface IInboxDispatchProps {
  getUserNotifications(payload: GetUserNotifications$Request): void
  setFeedFilter(payload: NotificationType): void
}

export default function Inbox(props: IInboxProps & IInboxDispatchProps) {
  const { notificationId, userNotifications, isLoading, isPush, feedFilter, getUserNotifications, setFeedFilter } =
    props

  const loading = !userNotifications && isLoading

  const visibleNotifications = useMemo(() => {
    const sorted = [...(userNotifications || [])].sort((a, b) => b.sentAt - a.sentAt)
    return sorted.filter((n) => n.type === feedFilter)
  }, [userNotifications, feedFilter])

  useEffect(() => {
    getUserNotifications({})

    let appStateChangeListener: PluginListenerHandle | undefined
    const addListener = async () => {
      appStateChangeListener = await App.addListener('appStateChange', async ({ isActive }) => {
        if (isActive) {
          getUserNotifications({})
        }
      })
    }
    addListener().catch(console.error)

    return () => {
      if (appStateChangeListener) {
        try {
          appStateChangeListener.remove()
        } catch (error) {
          console.error('Inbox | appStateChangeListener.remove() error', error)
        }
      }
    }
  }, [])

  useEffect(() => {
    if (isPush) {
      getUserNotifications({})
    }
  }, [isPush])

  const isFirstRun = React.useRef(true)
  useEffect(() => {
    if (isFirstRun.current || isPush) {
      isFirstRun.current = false
      if (notificationId) {
        // A deep-linked notification on the other tab could not scroll into view.
        const target = userNotifications.find((n) => n.notificationId === notificationId)
        if (target && target.type !== feedFilter) {
          setFeedFilter(target.type)
        }
        setTimeout(() => {
          const element = document.getElementById('n-' + String(notificationId))
          if (element) {
            if (isNative()) {
              const scrollableDiv = document.getElementById('content')
              if (scrollableDiv) {
                // TODO: Should we account for safe area of the header nav?
                // TODO: Wrong position for android
                const headerHeight = document.querySelectorAll<HTMLElement>('.layout__header')[0]?.offsetHeight || 0
                const elementTop = element.offsetTop - scrollableDiv.offsetTop + headerHeight - 15
                scrollableDiv.scrollTo({ top: elementTop, behavior: 'smooth' })
              }
            } else {
              element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
            }
          }
        }, 100)
      }
      return
    }
  }, [userNotifications, notificationId])

  return (
    <Card>
      <NotificationsWrapper />
      <YumHeader>
        <h1>
          {t`Notifications`}
          {!loading && (
            <span style={{ opacity: 0.5, whiteSpace: 'nowrap' }}>
              {' '}
              ({userNotifications.filter((n) => !n.read).length})
            </span>
          )}
        </h1>
        <YumButton variant="outlined" size="small" to={ROUTES.NOTIFICATION_SETTINGS.to} aria-label={t`Preferences`}>
          <FilterIconNew style={{ width: '1rem', height: '1rem' }} aria-hidden="true" />
          <Box
            display={{ xs: 'none', md: 'inline' }}
            style={isRtl ? { marginRight: '6px' } : { marginLeft: '6px' }}
          >{t`Preferences`}</Box>
        </YumButton>
      </YumHeader>
      <YumTabs
        onChange={(event: React.ChangeEvent<unknown>, newTab: NotificationType) => setFeedFilter(newTab)}
        value={feedFilter}
        style={{ marginBottom: '20px', backgroundColor: '#F5F5F9' }}
      >
        <YumTab label={t`Announcements`} value={NotificationType.ANNOUNCEMENT} />
        <YumTab label={t`Notifications`} value={NotificationType.NOTIFICATION} />
      </YumTabs>
      {loading ? (
        <Loader />
      ) : !visibleNotifications.length ? (
        <YumEmptyState
          text={
            feedFilter === NotificationType.ANNOUNCEMENT
              ? t`No announcements here yet...`
              : t`No notifications here yet...`
          }
          Icon={EmptyStateIcon}
        />
      ) : (
        <div>
          {visibleNotifications.map((n, i) => (
            <UserNotificationWrapper
              key={n.id}
              notification={n}
              isOpen={n.notificationId === notificationId}
              isLast={i === visibleNotifications.length - 1}
            />
          ))}
        </div>
      )}
    </Card>
  )
}
